import React from 'react';

import { Helmet } from 'react-helmet';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { Authenticated, NotAuthenticated } from '~/auth';
import { NavLink } from '~/lib/react-router-dom';

import { Alert, Box, Button, Card, Container, Toolbar } from '@mui/material';

import {
  AdGroupDetailsView,
  AdGroupListView,
  BidModificationHistory,
  CampaignDetailsView,
  CustomerCampaignListView,
  CustomerListView,
} from '~/components/GoogleAds';
import AdCreativeAIAssistantContainer from '~/components/GoogleAds/AdCreativeAIAssistantContainer';
import HotelBidFailureLog from '~/components/GoogleAdsHotelBidding/HotelBidFailureLog';
import HotelBidModificationHistory from '~/components/GoogleAdsHotelBidding/HotelBidModificationHistory';
import BusinessMainPage from '~/components/LeBusinessTraveller/pages/MainPage';
import usePromoAdminPermission from '~/components/Promo/hooks/usePromoPermission';
import ToursDepartureInsightsPage from '~/components/Tours/Pages/Lists/ToursDepartureInsightsPage';
import CreateOfferDeepLinkForm from '~/components/Users/CreateOfferDeepLinkForm';
import VendorInclusionsPage from '~/components/Vendors/Profile/VendorInclusionsPage';
import RoomTypesGroupsPageContainer from '~/components/Vendors/Properties/RoomTypesGroupsPageContainer';

import CommercialOpsPage from '~/pages/CommercialOps/CommercialOpsPage';
import ShopPage from '~/pages/Shop/Page';
import VoucherDisplayConfigDetailPage from '~/pages/VoucherDisplayConfig/VoucherDisplayConfigDetailPage';
import QuickCreateVoucherPage from '~/pages/Vouchers/QuickCreateVoucherPage';
import VoucherAutoAssignRuleFormPage from '~/pages/Vouchers/VoucherAutoAssignRuleFormPage';
import VoucherFormPage from '~/pages/Vouchers/VoucherFormPage';

import { ITEM_TYPE_GIFT_CARD, ITEM_TYPE_INSURANCE, ITEM_TYPE_OFFER, ITEM_TYPE_OFFLINE_FLIGHT } from '~/consts/order';
import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import isProd from '~/utils/isProd';

import AccommodationBundleCreatePage from './Accommodation/Pages/Bundles/AccommodationBundleCreatePage';
import AccommodationBundleEditPage from './Accommodation/Pages/Bundles/AccommodationBundleEditPage';
import AccommodationHomePage from './Accommodation/Pages/Home/AccommodationHomePage';
import AccommodationPropertyDetailsPage from './Accommodation/Pages/PropertyDetails/AccommodationPropertyDetailsPage';
import { AiChatDrawer, AiChatProvider } from './AiChat';
import AiSummaryStatsPage from './AiSummaryStats/AiSummaryStatsPage';
import AmenitiesPageContainer from './Amenities/AmenitiesPageContainer';
import AmenityEdit from './Amenities/AmenityEdit';
import AmenityGroupEdit from './Amenities/AmenityGroupEdit';
import AttractionDetailPage from './Attractions/AttractionDetailPage';
import AwsJobsPage from './AwsJobs/Page';
import BedbankPropertiesEditPage from './Bedbank/Properties/Edit/Page';
import BedbankPropertiesPage from './Bedbank/Properties/Home/Page';
import { BookingPage } from './Bookings/BookingPage';
import AppChrome from './Common/AppChrome';
import Footer from './Common/Footer';
import Spotlight from './Common/Spotlight/Spotlight';
import { RouteScanner, SpotlightProvider } from './Common/Spotlight/SpotlightProvider';
import CurationPageContainer from './Content/CurationPageContainer';
import CompaniesListPage from './CorporateEvents/pages/CompaniesListPage';
import CompanyFormPage from './CorporateEvents/pages/CompanyFormPage';
import EventFormPage from './CorporateEvents/pages/EventFormPage';
import EventsListPage from './CorporateEvents/pages/EventsListPage';
import CruiseBundlesCreate from './Cruises/pages/Bundles/BundlesCreate';
import CruiseBundlesEdit from './Cruises/pages/Bundles/BundlesEdit/BundlesEdit';
import CruiseLineEdit from './Cruises/pages/CruiseLines/CruiseLineEdit';
import CruiseLineOrdering from './Cruises/pages/CruiseLines/CruiseLineOrdering/CruiseLineOrdering';
import CruiseDealCrawlerCreate from './Cruises/pages/DealCrawlerPage/DealCrawlerCreate';
import DealCrawlerPageCreationConfirm from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/DealCrawlerPageCreationConfirm';
import DealCrawlerPageCreationSelection from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/DealCrawlerPageCreationSelection';
import {
  DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH,
  DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH,
} from './Cruises/pages/DealCrawlerPage/DealCrawlerPageCreation/paths';
import CruiseDealCrawlerView from './Cruises/pages/DealCrawlerPage/DealCrawlerView';
import DealPageCreate from './Cruises/pages/DealsPage/DealPageCreate';
import DealPageEdit from './Cruises/pages/DealsPage/DealPageEdit/DealPageEdit';
import DealPageOrdering from './Cruises/pages/DealsPage/DealPageOrdering';
import DealPageList from './Cruises/pages/DealsPage/components/DealPageList';
import CruisePageImages from './Cruises/pages/Home/CruisePageImages';
import CruisesPage from './Cruises/pages/Home/CruisesPage';
import CruisePageDetails from './Cruises/pages/Home/CruisesPageDetails';
import CruisesBookingLogsPage from './Cruises/pages/Home/components/Bookings/BookingLogsPage';
import CruisesBookingsPage from './Cruises/pages/Home/components/Bookings/BookingsPage';
import CruiseCabinCategoriesDetailsPage from './Cruises/pages/Home/components/CabinCategories/CabinCategoriesDetails';
import CabinCategoriesGroupOverviewPage from './Cruises/pages/Home/components/CabinCategoriesGroup/CabinCategoriesGroupOverview/CabinCategoriesGroupOverviewPage';
import CreateCabinCategoriesGroupPage from './Cruises/pages/Home/components/CabinCategoriesGroup/CreateCabinCategoriesGroup/CreateCabinCategoriesGroup';
import CruiseSailingDetailsPage from './Cruises/pages/Home/components/Sailings/SailingDetailsPage';
import CruiseInclusionsCreate from './Cruises/pages/Inclusions/InclusionsCreate';
import CruiseInclusionsEdit from './Cruises/pages/Inclusions/InclusionsEdit/InclusionsEdit';
import CruisePortsEditPage from './Cruises/pages/Ports/PortsEditPage';
import EditRateCodePage from './Cruises/pages/RateCodeManagement/EditRateCodePage';
import RateCodeManagementPage from './Cruises/pages/RateCodeManagement/RateCodeManagementPage';
import ShipImageEditor from './Cruises/pages/Ships/ShipImageEditor';
import HeroPlannerScheduleEditorPage from './CustomerCommunication/HeroPlanner/Pages/HeroPlannerScheduleEditorPage';
import HeroPlannerSchedulesListPage from './CustomerCommunication/HeroPlanner/Pages/HeroPlannerSchedulesListPage';
import MarketingEmailsPageContainer from './CustomerCommunication/MarketingEmails/MarketingEmailsPageContainer';
import RenderMarketingEmailPage from './CustomerCommunication/MarketingEmails/RenderMarketingEmail';
import EmailPage from './Emails/EmailPage';
import EmailPageContainer from './Emails/EmailPageContainer';
import RenderEmail from './Emails/RenderEmail';
import ExperiencesPage from './Experiences';
import AttractionImageEditor from './Experiences/Attractions/AttractionImageEditor';
import FinancePageContainer from './Finance/FinancePageContainer';
import FinanceOfferDetailContainer from './Finance/OfferDetail/OfferDetail';
import VendorBookingPaymentsContainer from './Finance/OfferEdit/VendorBookingPaymentsContainer';
import VendorOfferPaymentsContainer from './Finance/OfferEdit/VendorOfferPaymentsContainer';
import CreditReservationDetailContainer from './Flights/CreditReservationDetailContainer';
import CreditReservationEditContainer from './Flights/CreditReservationEditContainer';
import CreditReservationListContainer from './Flights/CreditReservationListContainer';
import FlightsDealsEdit from './Flights/FlightDeals/FlightDealsEdit';
import FlightsCampaignsEdit from './Flights/FlightsCampaigns/FlightsCampaignsEdit';
import { FlightsLogsViewer } from './Flights/FlightsLogsViewer/FlightsLogsViewer';
import FlightsPage from './Flights/FlightsPageContainer';
import HealthPage from './Health/HealthPage';
import IndexPage from './IndexPage';
import InvoiceToolsPageContainer from './InvoiceTools/InvoiceToolsPageContainer';
import LereAdminPageContainer from './LERE/LereAdminPageContainer';
import BusinessTabContainer from './LeBusinessTraveller/pages/BusinessDetailsPage/TabContainer';
import LoyaltyPageContainer from './Loyalty/LoyaltyPageContainer';
import BadgesContainer from './Marketing/Badges/BadgesContainer';
import BadgesDetailContainer from './Marketing/Badges/BadgesDetailContainer';
import CompedPackagesDetailContainer from './Marketing/CompedPackages/CompedPackagesDetailContainer';
import MarketingPage from './Marketing/MarketingPage';
import OfferDetailContainer from './Offers/Detail/OfferDetailContainer';
import BadgesEditContainer from './Offers/Edit/BadgesEditContainer';
import BundlePackagesEditContainer from './Offers/Edit/BundlePackagesEditContainer';
import ExperiencesEditContainer from './Offers/Edit/ExperiencesEditContainer';
import ImagesEditContainer from './Offers/Edit/ImagesEditContainer';
import LuxMomentsPage from './Offers/Edit/LuxMomentsPage';
import NewScheduleContainer from './Offers/Edit/NewScheduleContainer';
import OfferEditContainer from './Offers/Edit/OfferEditContainer';
import RoomPolicyEditContainer from './Offers/Edit/RoomPoliciesEditContainer';
import ScheduleEditContainer from './Offers/Edit/ScheduleEditContainer';
import OfferSearchOrderingContainer from './Offers/Edit/SearchOrderingContainer';
import TravellerDetailRequirementsEditPage from './Offers/Edit/TravellerDetailRequirements';
import VideosEditContainer from './Offers/Edit/VideosEditContainer';
import OffersPage from './Offers/Home/OffersPage';
import OpportunityContainer from './Opportunity/OpportunityContainer';
import ExamplePage from './Page/Example/ExamplePage';
import ScheduledPriceSummaryJobDetails from './PriceParrot/ScheduledPriceSummaryJobDetails';
import LoginPage from './Profile/LoginPage';
import ProfilePage from './Profile/ProfilePage';
import OrderEditContainer from './Purchases/Edit/OrderEditContainer';
import PurchasesPageBookingNumberContainer from './Purchases/Home/PurchasesPageBookingNumberContainer';
import PurchasesPageContainer from './Purchases/Home/PurchasesPageContainer';
import OrderAddNotePage from './Purchases/OrderDetail/OrderAddNotePage';
import OrderAtolPageContainer from './Purchases/OrderDetail/OrderAtolPageContainer';
import OrderBookingContainer from './Purchases/OrderDetail/OrderBookingPageContainer';
import OrderGiftEditPage from './Purchases/OrderDetail/OrderGiftEditPage';
import OrderPaymentSurcharge from './Purchases/OrderDetail/OrderPaymentSurcharge';
import SmsVerifyStatsPage from './SmsVerifyStats/SmsVerifyStatsPage';
import UpdateCsAgentsPage from './SmsVerifyStats/UpdateCsAgentsPage';
import SupportAssistantPage from './SupportAssistant/pages/Home/SupportAssistantPage';
import UpdateMonitor from './System/UpdateMonitor';
import TourDealPageCreate from './Tours/Pages/Deals/DealPageCreate';
import TourDealPageEdit from './Tours/Pages/Deals/DealPageEdit';
import TourDealsPage from './Tours/Pages/Deals/TourDealsPage';
import ToursLEListPage from './Tours/Pages/Lists/ToursLEListPage';
import ToursManifestPage from './Tours/Pages/Lists/ToursManifestPage';
import ToursNotificationsPage from './Tours/Pages/Lists/ToursNotificationsPage';
import ToursTTCListPage from './Tours/Pages/Lists/ToursTTCListPage';
import TourDetailsPage from './Tours/Pages/TourDetailsPage';
import CreateTripPage from './TripPlanner/CreateTripPage';
import TripDetailPage from './TripPlanner/TripDetailPage';
import TripPlannerChatPage from './TripPlanner/TripPlannerChatPage';
import TripPlannerClassicPage, { classicTripPlannerTabs } from './TripPlanner/TripPlannerClassicPage';
import TripPlannerPage from './TripPlanner/TripPlannerPage';
import { tripPlannerChatTabs } from './TripPlanner/tripPlannerChatTabs';
import CreditsContainer from './Users/Credits/CreditsContainer';
import FindByPhone from './Users/FindByPhone/FindByPhone';
import UserHistory from './Users/History/UserHistory';
import LoyaltyContainer from './Users/Loyalty/Container';
import AddItemPageContainer from './Users/Orders/AddItem/AddItemPageContainer';
import AtolCertificateDetailPage from './Users/Orders/Atol/AtolCertificateDetailPage';
import AtolCertificateEditPage from './Users/Orders/Atol/AtolCertificateEditPage';
import AtolPage from './Users/Orders/Atol/AtolPage';
import NewOrderPageContainer from './Users/Orders/NewOrder/NewOrderPageContainer';
import UpdateInsuranceContainer from './Users/Orders/UpdateInsurance';
import UserPage from './Users/UserPage';
import UsersPageContainer from './Users/UsersPageContainer';
import { VendorAddonDetails } from './Vendors/Addons/AddonDetails';
import { VendorAddonEdit } from './Vendors/Addons/AddonEdit';
import EditChainSettingsPage from './Vendors/ChainSettings/EditChainSettingsPage';
import ChainDetailPage from './Vendors/Chains/ChainDetailPage';
import ChainsListPage from './Vendors/Chains/ChainsListPage';
import VendorsPageContainer from './Vendors/Home/VendorsPageContainer';
import VendorsPartnerCentralPage from './Vendors/PartnerCentral/VendorsPartnerCentralPage';
import ChainsDetailContainer from './Vendors/Profile/ChainsDetailContainer';
import VendorsDetailContainer from './Vendors/Profile/VendorsDetailContainer';
import CuratedContentEditPage from './Vendors/Properties/CuratedContentEditPage';
import ImagesEditPage from './Vendors/Properties/ImagesEditPage';
import PropertyCommissionableTaxesContentEditPage from './Vendors/Properties/PropertyCommissionableTaxesContentEditPage';
import PropertyEditContainer from './Vendors/Properties/PropertyEditContainer';
import PropertyNewContainer from './Vendors/Properties/PropertyNewContainer';
import PropertyPageContainer from './Vendors/Properties/PropertyPageContainer';
import PropertyReviewsEditContainer from './Vendors/Properties/PropertyReviewsEditContainer';
import PropertyTaxesContentEditPage from './Vendors/Properties/PropertyTaxesContentEditPage';
import PropertyTaxesFeesEditPage from './Vendors/Properties/PropertyTaxesFeesEditPage';
import RoomTypeDatesEditContainer from './Vendors/Properties/RoomTypeDatesEditContainer';
import RoomTypeEditContainer from './Vendors/Properties/RoomTypeEditContainer';
import RoomTypesEditContainer from './Vendors/Properties/RoomTypesEditContainer';
import VideosEditPage from './Vendors/Properties/VideosEditPage';
import RatePlanEditContainer from './Vendors/RatePlans/RatePlanEditContainer';
import RatePlanInclusionsPage from './Vendors/RatePlans/RatePlanInclusionsPage';
import RatePlanNewContainer from './Vendors/RatePlans/RatePlanNewContainer';
import RatePlanPageContainer from './Vendors/RatePlans/RatePlanPageContainer';
import RatePlansGroupEditContainer from './Vendors/RatePlansGroups/RatePlansGroupEditContainer';
import RoomTypesGroupEditContainer from './Vendors/RoomTypesGroups/RoomTypesGroupEditContainer';
import TourEditContainer from './Vendors/Tours/TourEditContainer';
import TourLegsEditContainer from './Vendors/Tours/TourLegsEditContainer';
import TourNewContainer from './Vendors/Tours/TourNewContainer';
import TourOptionDatesEditContainer from './Vendors/Tours/TourOptionDatesEditContainer';
import TourOptionsEditContainer from './Vendors/Tours/TourOptionsEditContainer';
import TourPageContainer from './Vendors/Tours/TourPageContainer';
import TourReviewsEditContainer from './Vendors/Tours/TourReviewsEditContainer';
import MediaPreviewPage from './Videos/MediaPreviewlPage';
import VideoEditPage from './Videos/VideoEditPage';
import {
  AIImageManagerPage,
  AccountDeletionsPage,
  AdminUsersPageContainer,
  AgenciesPage,
  AuditLogSearch,
  BannerUploadPage,
  BriefCasePage,
  BriefCaseTemplatePage,
  BulkCreditAllocationPage,
  BulkVccReplacementPage,
  CSAnalysisPage,
  CreditReversalsPage,
  CustomOffersCreatePage,
  CustomOffersEditPage,
  FindSimilarAccountsPage,
  ImageUploaderUtilityPage,
  LabPage,
  LeDataPage,
  OpenAIPage,
  OrderDetailContainer,
  PackagesEditContainer,
  PermissionsContainer,
  PromoDetailContainerV3,
  ReservationRemediation,
  SalesFxHedging,
  Settings,
  SiteBannerCampaignCreate,
  SiteBannerCampaignEdit,
  SiteBannerCampaignsPage,
  StoplistPage,
  StructuredFinePrintPage,
  SystemPageContainer,
  SystemUserPage,
  TrainingAccountProvisioningPage,
} from './lazyRoutes';

const TOURS_SUB_NAVIGATIONS: Array<App.NavigationLink> = [
  {
    href: '/tours/lists/le',
    label: 'LE Tours',
  },
  {
    href: '/tours/lists/ttc',
    label: 'TTC Tours',
  },
  {
    href: '/tours/departures/insights',
    label: 'Departures insights',
  },
  {
    href: '/tours/deals',
    label: 'Deals',
  },
  {
    href: '/tours/manifests',
    label: 'Departure Manifests',
  },
  {
    href: '/tours/notifications',
    label: 'DMC Notifications',
  },
];

function isToursNavigationSelected(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function MasterPage() {
  const { tenant } = useCurrentTenant();
  const location = useLocation();
  const isPromoAdmin = usePromoAdminPermission();
  const isProduction = isProd();

  const { pathname } = location;

  const promoAdminOnlyDenied = (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Alert severity="error">
        This page is in testing and only available to users with the {ROLE_PROMO_ADMIN_USER} role. For access, contact
        the growth team.
      </Alert>
    </Container>
  );

  return (
    <Box className="MasterPage" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Helmet titleTemplate="%s" defaultTitle={`Admin Portal | ${tenant.title}`} />

      <AppChrome tenant={tenant}>
        <UpdateMonitor />

        <Box flexGrow={1}>
          <Authenticated>
            <AiChatProvider>
              <SpotlightProvider>
                <Spotlight />
                <RouteScanner>
                  <Switch>
                    <Route exact path="/" component={IndexPage} />
                    <Redirect exact from="/curation" to="/curation/offer-list-order" />
                    <Redirect exact from="/search-curation" to="/search-curation/search-dashboard" />
                    <Redirect exact from="/curation/search-dashboard" to="/search-curation/search-dashboard" />
                    <Redirect exact from="/curation/search-ranking" to="/search-curation/search-ranking" />
                    <Redirect exact from="/curation/blocked-offers" to="/search-curation/blocked-offers" />
                    <Redirect
                      exact
                      from="/curation/property-location-override"
                      to="/search-curation/property-location-override"
                    />
                    <Redirect exact from="/curation/nl-search" to="/search-curation/nl-search" />
                    <Redirect
                      exact
                      from="/curation/restore-search-dashboard"
                      to="/search-curation/restore-search-dashboard"
                    />
                    <Route exact path="/curation/:tab" component={CurationPageContainer} />
                    <Route exact path="/search-curation/:tab" component={CurationPageContainer} />
                    <Route exact path="/experiences" component={ExperiencesPage} />
                    <Route exact path="/experience/:id_experience" component={ExperiencesPage} />
                    <Route exact path="/offers/:id_offer" component={OfferDetailContainer} />
                    <Route exact path="/offers" component={OffersPage} />
                    <Route exact path="/trip-planner" component={TripPlannerPage} />
                    <Route exact path="/trip-planner/trip/:tripId" component={TripDetailPage} />
                    <Route exact path="/lab" component={LabPage} />
                    <Route exact path="/openai" component={OpenAIPage} />
                    <Route exact path="/ask-richard/shared/:sessionId" component={LeDataPage} />
                    <Route exact path="/ask-richard" component={LeDataPage} />
                    <Redirect exact from="/data-chatbot" to="/ask-richard" />
                    <Route exact path="/attractions/:attractionId" component={AttractionDetailPage} />
                    <Route exact path="/attractions/:attractionId/image" component={AttractionImageEditor} />
                    <Route exact path="/shop" component={ShopPage} />
                    <Redirect exact from="/trip-planner/classic" to={classicTripPlannerTabs[0].path} />
                    {classicTripPlannerTabs.map((tab) => (
                      <Route key={tab.path} exact path={tab.path} component={TripPlannerClassicPage} />
                    ))}
                    <Redirect exact from="/trip-planner/chat" to={tripPlannerChatTabs[0].path} />
                    {tripPlannerChatTabs.map((tab) => (
                      <Route key={tab.path} exact path={tab.path} component={TripPlannerChatPage} />
                    ))}
                    <Route exact path="/trip-planner/classic/trip/:tripId" component={TripDetailPage} />
                    <Redirect exact from="/trip-planner/trip/:tripId" to="/trip-planner/classic/trip/:tripId" />

                    <Route exact path="/briefcase" component={BriefCasePage} />
                    <Route exact path="/briefcase/:jobId" component={BriefCasePage} />
                    <Route exact path="/briefcase-template" component={BriefCaseTemplatePage} />
                    <Route exact path="/edit-offers/:id_offer" component={OfferEditContainer} />

                    <Route
                      exact
                      path="/edit-offers/:vertical/:id_offer/search-orderings"
                      component={OfferSearchOrderingContainer}
                    />

                    <Route exact path="/edit-offers/:id_offer/schedules/:brand" component={ScheduleEditContainer} />

                    <Route exact path="/edit-offers/:id_offer/schedules" component={NewScheduleContainer} />
                    <Route
                      exact
                      path="/edit-offers/:id_offer/bundle-packages"
                      component={BundlePackagesEditContainer}
                    />
                    <Route exact path="/edit-offers/:id_offer/packages" component={PackagesEditContainer} />
                    <Route exact path="/edit-offers/:id_offer/images" component={ImagesEditContainer} />
                    <Route exact path="/edit-offers/:id_offer/videos" component={VideosEditContainer} />
                    <Route exact path="/edit-offers/:id_offer/lux-moments" component={LuxMomentsPage} />

                    <Route exact path="/edit-offers/:id_offer/room-policies" component={RoomPolicyEditContainer} />

                    <Route
                      exact
                      path="/edit-offers/:id_offer/structured-fine-print"
                      component={StructuredFinePrintPage}
                    />

                    <Route exact path="/edit-offers/:id_offer/badges" component={BadgesEditContainer} />

                    <Route
                      exact
                      path="/edit-offers/:id_offer/traveller-detail-requirements"
                      component={TravellerDetailRequirementsEditPage}
                    />

                    <Route exact path="/edit-offers/:id_offer/experiences" component={ExperiencesEditContainer} />

                    <Route path="/tours">
                      <Container sx={{ mb: 2 }}>
                        <Card variant="outlined">
                          <Toolbar>
                            {TOURS_SUB_NAVIGATIONS.map((nav) => {
                              const isSelected = isToursNavigationSelected(pathname, nav.href);

                              return (
                                <Button
                                  key={nav.href}
                                  component={NavLink}
                                  to={nav.href}
                                  color={isSelected ? 'primary' : 'secondary'}
                                  variant={isSelected ? 'contained' : 'text'}
                                  size="large"
                                  sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                                >
                                  {nav.label}
                                </Button>
                              );
                            })}
                          </Toolbar>
                        </Card>
                      </Container>

                      <Switch>
                        <Redirect exact from="/tours" to="tours/lists/le" />
                        <Route exact path="/tours/lists/le" component={ToursLEListPage} />
                        <Route exact path="/tours/lists/ttc" component={ToursTTCListPage} />
                        <Route exact path="/tours/deals" component={TourDealsPage} />
                        <Route exact path="/tours/deals/create" component={TourDealPageCreate} />
                        <Route exact path="/tours/deals/:id/edit" component={TourDealPageEdit} />
                        <Route exact path="/tours/departures/insights" component={ToursDepartureInsightsPage} />
                        <Route exact path="/tours/manifests" component={ToursManifestPage} />
                        <Route exact path="/tours/notifications" component={ToursNotificationsPage} />
                        <Route path="/tours/details/:tour_id" component={TourDetailsPage} />
                        {/* Backwards compatibility for the Chrome Extension */}
                        <Redirect from="/tours/:tour_id" to="/tours/details/:tour_id" />
                      </Switch>
                    </Route>

                    <Route exact path="/aws/jobs" component={AwsJobsPage} />
                    <Route exact path="/bedbank/properties" component={BedbankPropertiesPage} />
                    <Route exact path="/bedbank/properties/:id" component={BedbankPropertiesEditPage} />
                    <Route exact path="/flights" component={FlightsPage} />
                    <Route exact path="/flights/credit-reservation" component={CreditReservationListContainer} />
                    <Route
                      exact
                      path="/flights/credit-reservation/:id/details"
                      component={CreditReservationDetailContainer}
                    />
                    <Route
                      exact
                      path="/flights/credit-reservation/:id/edit"
                      component={CreditReservationEditContainer}
                    />
                    <Route exact path="/flights/flight-deal/:id/edit" component={FlightsDealsEdit} />
                    <Route exact path="/flights/campaigns/:id" component={FlightsCampaignsEdit} />
                    <Route exact path="/flights/xml-logs-viewer/:id" component={FlightsLogsViewer} />

                    <Redirect exact from="/users-list" to="/system" />
                    <Redirect exact from="/users-list/stop-list/:id?" to="/system/stop-list" />
                    <Route exact path="/agencies" render={() => <AgenciesPage />} />
                    <Route
                      exact
                      path="/agencies/:tab(agencies|agency-submission|affiliations)?"
                      render={() => <AgenciesPage />}
                    />
                    <Route
                      exact
                      path="/users-list/:tab(agency-submission|agencies|affiliations)?/:id?"
                      component={UsersPageContainer}
                    />

                    <Route exact path="/find-by-phone/:phone?" component={FindByPhone} />

                    <Route exact path="/users/:id_user/custom-offers/create" component={CustomOffersCreatePage} />
                    <Route exact path="/users/:id_user/custom-offers/:offer_id" component={CustomOffersEditPage} />

                    <Route exact path="/users/:id_user/credits" component={CreditsContainer} />
                    <Route exact path="/users/:id_user/loyalty/:code" component={LoyaltyContainer} />
                    <Route exact path="/users/:user_email" component={UserPage} />
                    <Route exact path="/users/:id_user/history" component={UserHistory} />
                    <Route
                      exact
                      path="/users/:id_user/new-order/:step?"
                      render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_OFFER} {...props} />}
                    />
                    <Route
                      exact
                      path="/users/:id_user/new-flight-order/:step?"
                      render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_OFFLINE_FLIGHT} {...props} />}
                    />
                    <Route
                      exact
                      path="/users/:id_user/new-gift-card-order/:step?"
                      render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_GIFT_CARD} {...props} />}
                    />
                    <Route
                      exact
                      path="/users/:id_user/new-insurance-order/:step?"
                      render={(props) => <NewOrderPageContainer orderType={ITEM_TYPE_INSURANCE} {...props} />}
                    />
                    <Route
                      exact
                      path="/users/:id_user/edit-orders/:id_orders/add-item/:step?"
                      component={AddItemPageContainer}
                    />
                    <Route
                      path="/users/:id_user/edit-orders/:id_order/insurance/:id/:step"
                      component={UpdateInsuranceContainer}
                      exact
                    />
                    <Route exact path="/users/:id_user/atol" component={AtolPage} />
                    <Route exact path="/atol-certificate/:id_atol_certificate" component={AtolCertificateDetailPage} />
                    <Route
                      exact
                      path="/atol-certificate/:id_atol_certificate/edit"
                      component={AtolCertificateEditPage}
                    />

                    <Route exact path="/purchases" component={PurchasesPageContainer} />
                    <Route exact path="/purchases/section/:section" component={PurchasesPageContainer} />
                    <Route
                      exact
                      path="/purchases/booking-number/:bookingNumber"
                      component={PurchasesPageBookingNumberContainer}
                    />

                    <Route exact path="/purchases/:id_orders/payment" component={OrderPaymentSurcharge} />
                    <Route exact path="/purchases/:id_orders/gift/edit" component={OrderGiftEditPage} />
                    <Route exact path="/purchases/:id_orders/notes/add" component={OrderAddNotePage} />
                    <Route path="/purchases/:id_orders/booking-details" component={OrderBookingContainer} />
                    <Route path="/purchases/:id_orders/atol-details" component={OrderAtolPageContainer} />
                    <Route path="/purchases/:id_orders" component={OrderDetailContainer} />
                    <Route exact path="/edit-purchases/:id_orders" component={OrderEditContainer} />
                    <Route exact path="/system/users/:id_user" component={SystemUserPage} />
                    <Route exact path="/system" component={SystemPageContainer} />
                    <Route exact path="/system/find-similar-accounts" component={FindSimilarAccountsPage} />
                    <Route exact path="/system/account-deletions" component={AccountDeletionsPage} />
                    <Route exact path="/system/bulk-credit-allocation" component={BulkCreditAllocationPage} />
                    <Route exact path="/system/bulk-vcc-replacement" component={BulkVccReplacementPage} />
                    <Route exact path="/system/credit-reversals" component={CreditReversalsPage} />
                    <Route exact path="/system/cs-analysis" component={CSAnalysisPage} />
                    <Route exact path="/system/admin-users" component={AdminUsersPageContainer} />
                    <Route exact path="/system/stop-list" component={StoplistPage} />
                    <Route exact path="/system/reservation-remediation" component={ReservationRemediation} />
                    <Route exact path="/system/audit-logs" component={AuditLogSearch} />
                    <Route exact path="/system/permissions/:role" component={PermissionsContainer} />
                    <Route exact path="/system/settings" component={Settings} />
                    <Route exact path="/system/image-uploader" component={ImageUploaderUtilityPage} />
                    <Route exact path="/system/ai-image-manager/:tab?/:batchId?" component={AIImageManagerPage} />
                    <Route exact path="/system/sales-fx-hedging" component={SalesFxHedging} />
                    <Route exact path="/system/targeted-banner-campaigns" component={SiteBannerCampaignsPage} />
                    <Route exact path="/system/targeted-banner-campaigns/create" component={SiteBannerCampaignCreate} />
                    <Route
                      exact
                      path="/system/targeted-banner-campaigns/:campaignId"
                      component={SiteBannerCampaignEdit}
                    />

                    {!isProduction && (
                      <Route
                        exact
                        path="/system/training-account-provisioning"
                        component={TrainingAccountProvisioningPage}
                      />
                    )}
                    <Route exact path="/lab" component={LabPage} />

                    <Route exact path="/vendors" component={VendorsPageContainer} />
                    <Route exact path="/vendors/chains" component={ChainsListPage} />
                    <Route exact path="/vendors/chains/:chainId" component={ChainDetailPage} />
                    <Route exact path="/vendors/:id_vendor" component={VendorsDetailContainer} />
                    <Route exact path="/vendors/:id_vendor/partner-central" component={VendorsPartnerCentralPage} />
                    <Route exact path="/vendors/chain/:id_parent" component={ChainsDetailContainer} />
                    <Route exact path="/vendors/chain/:id_parent/settings" component={EditChainSettingsPage} />
                    <Route exact path="/vendors/:id_vendor/properties" component={PropertyNewContainer} />
                    <Route exact path="/vendors/:id_vendor/properties/:id_property" component={PropertyPageContainer} />
                    <Route
                      exact
                      path="/vendors/:id_vendor/edit-property/:id_property"
                      component={PropertyEditContainer}
                    />
                    <Route exact path="/vendors/:id_vendor/inclusions" component={VendorInclusionsPage} />
                    <Route exact path="/vendors/:id_vendor/rate-plans-groups" component={RatePlansGroupEditContainer} />
                    <Route exact path="/vendors/:id_vendor/rate-plans" component={RatePlanNewContainer} />
                    <Route
                      exact
                      path="/vendors/:id_vendor/rate-plans/:id_rate_plan"
                      component={RatePlanPageContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/rate-plans/:id_rate_plan/inclusions"
                      component={RatePlanInclusionsPage}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/edit-rate-plan/:id_rate_plan"
                      component={RatePlanEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-curated-content"
                      component={CuratedContentEditPage}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-room-types"
                      component={RoomTypesEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-images"
                      component={ImagesEditPage}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-videos"
                      component={VideosEditPage}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate"
                      component={RoomTypeEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/room-types/:id_room_type/room-rates/:id_room_rate/dates"
                      component={RoomTypeDatesEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/room-types-groups"
                      component={RoomTypesGroupsPageContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-room-types-groups"
                      component={RoomTypesGroupEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-reviews"
                      component={PropertyReviewsEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-content"
                      component={PropertyTaxesContentEditPage}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-commissionable-taxes-content"
                      component={PropertyCommissionableTaxesContentEditPage}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/properties/:id_property/edit-property-taxes-fees"
                      component={PropertyTaxesFeesEditPage}
                    />
                    <Route exact path="/vendors/:id_vendor/tours/:id_tour" component={TourPageContainer} />
                    <Route exact path="/vendors/:id_vendor/edit-tour/:id_tour" component={TourEditContainer} />
                    <Route exact path="/vendors/:id_vendor/tours" component={TourNewContainer} />
                    <Route
                      exact
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-options"
                      component={TourOptionsEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/tours/:id_tour/tour-options/:id_tour_option/dates"
                      component={TourOptionDatesEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-legs"
                      component={TourLegsEditContainer}
                    />
                    <Route
                      exact
                      path="/vendors/:id_vendor/tours/:id_tour/edit-tour-reviews"
                      component={TourReviewsEditContainer}
                    />
                    <Route exact path="/vendors/:id_vendor/addons/:id_addon" component={VendorAddonDetails} />
                    <Route
                      exact
                      path="/vendors/:id_vendor/addons/:id_addon/deal-options/:id_addon_deal_option/edit"
                      component={VendorAddonEdit}
                    />

                    <Route exact path="/bookings/:id_orders" component={BookingPage} />

                    <Route
                      exact
                      path="/marketing/voucher-display-config/new"
                      component={VoucherDisplayConfigDetailPage}
                    />
                    <Route
                      exact
                      path="/marketing/voucher-display-config/:voucherDisplayConfigId"
                      component={VoucherDisplayConfigDetailPage}
                    />
                    <Route exact path="/marketing/vouchers/new" component={VoucherFormPage} />
                    <Route
                      exact
                      path="/marketing/vouchers/quick-create"
                      render={() => (isPromoAdmin ? <QuickCreateVoucherPage /> : promoAdminOnlyDenied)}
                    />
                    <Route exact path="/marketing/vouchers/:voucherId" component={VoucherFormPage} />
                    <Route
                      exact
                      path="/marketing/voucher-auto-assign-rules/create"
                      render={() => (isPromoAdmin ? <VoucherAutoAssignRuleFormPage /> : promoAdminOnlyDenied)}
                    />
                    <Route
                      exact
                      path="/marketing/voucher-auto-assign-rules/:ruleId"
                      render={() => (isPromoAdmin ? <VoucherAutoAssignRuleFormPage /> : promoAdminOnlyDenied)}
                    />
                    <Route path="/marketing/:tab?" component={MarketingPage} exact />
                    <Route exact path="/marketing/video-gen/edit" component={VideoEditPage} />
                    <Route
                      exact
                      path="/marketing/video-gen/preview/output-video/:videoId"
                      component={MediaPreviewPage}
                    />
                    <Route exact path="/marketing/video-gen/preview/asset/:videoId" component={MediaPreviewPage} />
                    <Route path="/marketing/google-ads/customer/" component={CustomerListView} exact />
                    <Route path="/marketing/google-ads/creative-ai" component={AdCreativeAIAssistantContainer} exact />
                    <Route
                      path="/marketing/google-ads/bid-modification-history/"
                      component={BidModificationHistory}
                      exact
                    />
                    <Route
                      path="/marketing/google-ads/hotel-bid-modification-history/"
                      component={HotelBidModificationHistory}
                      exact
                    />
                    <Route path="/marketing/google-ads/hotel-bid-failure-log" component={HotelBidFailureLog} exact />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign"
                      component={CustomerCampaignListView}
                      exact
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId"
                      component={CampaignDetailsView}
                      exact
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group"
                      component={AdGroupListView}
                      exact
                    />
                    <Route
                      path="/marketing/google-ads/customer/:customerId/campaign/:campaignId/ad-group/:adGroupId"
                      component={AdGroupDetailsView}
                      exact
                    />
                    <Route path="/accommodation/properties" component={AccommodationHomePage} exact />
                    <Route path="/accommodation/properties/:propertyId" component={AccommodationPropertyDetailsPage} />
                    <Route path="/accommodation/bundles/new" component={AccommodationBundleCreatePage} exact />
                    <Route path="/accommodation/bundles/:bundleId/:tab?" component={AccommodationBundleEditPage} />
                    <Route path="/email/render" component={RenderEmail} exact />
                    <Route path="/email/sent" component={EmailPageContainer} exact />

                    <Route exact path="/comped/:id_comped" component={CompedPackagesDetailContainer} />
                    <Route exact path="/promos/code/:id_code" component={PromoDetailContainerV3} />
                    <Route exact path="/promos/:id_promo" component={PromoDetailContainerV3} />
                    <Route exact path="/badges" component={BadgesContainer} />
                    <Route exact path="/badges/:id_badge" component={BadgesDetailContainer} />

                    <Route exact path="/profile" component={ProfilePage} />
                    <Redirect exact from="/loyalty" to="/societe/custom-rule" />
                    <Redirect from="/loyalty/:section" to="/societe/:section" />
                    <Redirect exact from="/societe" to="/societe/custom-rule" />
                    <Route exact path="/societe/:section" component={LoyaltyPageContainer} />
                    <Route exact path="/finance" component={FinancePageContainer} />
                    <Route exact path="/finance/offers/:id_offer" component={FinanceOfferDetailContainer} />
                    <Route
                      exact
                      path="/finance/offers/:id_offer/offer-payments/:id_vendor/:id_offer_payment"
                      component={VendorOfferPaymentsContainer}
                    />
                    <Route
                      exact
                      path="/finance/offers/:id_offer/offer-payments/:id_vendor"
                      component={VendorOfferPaymentsContainer}
                    />
                    <Route
                      exact
                      path="/finance/offers/:id_offer/booking-payments/:id_vendor"
                      component={VendorBookingPaymentsContainer}
                    />
                    <Route path="/finance/:section/:report" component={FinancePageContainer} />
                    <Route path="/finance/:section" component={FinancePageContainer} />

                    <Route exact path="/email" component={EmailPage} />
                    <Route path="/email/:tab?" component={EmailPage} exact />

                    <Route
                      exact
                      path="/customer-communication/hero-planner/schedules/:scheduleEditorId"
                      component={HeroPlannerScheduleEditorPage}
                    />
                    <Route
                      exact
                      path="/customer-communication/hero-planner/schedules"
                      component={HeroPlannerSchedulesListPage}
                    />
                    <Route exact path="/customer-communication/banner-upload" component={BannerUploadPage} />
                    <Route exact path="/customer-communication/email/sent" component={MarketingEmailsPageContainer} />
                    <Route exact path="/customer-communication/email/render" component={RenderMarketingEmailPage} />

                    <Route exact path="/health" component={HealthPage} />

                    <Route exact path="/cruises/:section" component={CruisesPage} />
                    <Route exact path="/cruises/offer/:id_offer" component={CruisePageDetails} />
                    <Route exact path="/cruises/offer/:offerId/images" component={CruisePageImages} />
                    <Route exact path="/lere" component={LereAdminPageContainer} />
                    <Route exact path="/lere/:section" component={LereAdminPageContainer} />
                    <Route exact path="/cruises/ships/:shipId" component={ShipImageEditor} />
                    <Route exact path="/cruises/ships/:shipId/:tab(images|amenities)" component={ShipImageEditor} />
                    <Route exact path="/cruises/cruise-lines/order" component={CruiseLineOrdering} />
                    <Route exact path="/cruises/cruise-lines/:cruiseLineId" component={CruiseLineEdit} />
                    <Route exact path="/cruises/ports/:portId" component={CruisePortsEditPage} />
                    <Route exact path="/cruises/rate-code-management" component={RateCodeManagementPage} />
                    <Route exact path="/cruises/rate-code-management/:rateCodeId/edit" component={EditRateCodePage} />
                    <Route exact path="/cruises/sailings/:id_sailing" component={CruiseSailingDetailsPage} />
                    <Route
                      exact
                      path="/cruises/cabin-categories/:id_cabin"
                      component={CruiseCabinCategoriesDetailsPage}
                    />
                    <Route
                      exact
                      path="/cruises/cabin-categories-groups/create"
                      component={CreateCabinCategoriesGroupPage}
                    />
                    <Route exact path="/cruises/bundles/create" component={CruiseBundlesCreate} />
                    <Route exact path="/cruises/bundles/edit/:bundleId" component={CruiseBundlesEdit} />
                    <Route
                      exact
                      path="/cruises/bundles/edit/:bundleId/:tab(items|filters)/:action(create|edit)?/:actionId?"
                      component={CruiseBundlesEdit}
                    />
                    <Route exact path="/cruises/inclusions/create" component={CruiseInclusionsCreate} />
                    <Route exact path="/cruises/inclusions/edit/:inclusionId" component={CruiseInclusionsEdit} />
                    <Route
                      exact
                      path="/cruises/inclusions/edit/:inclusionId/:tab(items|filters)/:action(create|edit)?/:actionId?"
                      component={CruiseInclusionsEdit}
                    />
                    <Route exact path="/cruises/deals" component={DealPageList} />
                    <Route exact path="/cruises/deals/create" component={DealPageCreate} />
                    <Route exact path="/cruises/deals/ordering" component={DealPageOrdering} />
                    <Route exact path="/cruises/deals/:id/edit" component={DealPageEdit} />
                    <Route exact path="/cruises/deals/:id/edit/inclusions/:action?" component={DealPageEdit} />
                    <Route
                      exact
                      path="/cruises/cabin-categories-groups/:id_group"
                      component={CabinCategoriesGroupOverviewPage}
                    />
                    <Route exact path="/cruises/bookings/:booking_id/logs" component={CruisesBookingLogsPage} />
                    <Route exact path="/cruises/booking-logs" component={CruisesBookingsPage} />
                    <Route exact path="/cruises/deal-crawler/create" component={CruiseDealCrawlerCreate} />
                    <Route exact path="/cruises/deal-crawler/:id/view" component={CruiseDealCrawlerView} />
                    <Route
                      exact
                      path={DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}
                      component={DealCrawlerPageCreationSelection}
                    />
                    <Route
                      exact
                      path={DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}
                      component={DealCrawlerPageCreationConfirm}
                    />

                    <Route exact path="/lebusiness" component={BusinessMainPage} />
                    <Route exact path="/lebusiness/:businessId/:tab" component={BusinessTabContainer} />

                    <Redirect exact from="/invoice-tools" to="/invoice-tools/invoice-generator" />
                    <Route exact path="/invoice-tools/:tab" component={InvoiceToolsPageContainer} />

                    <Route exact path="/support-assistant/:tab?" component={SupportAssistantPage} />

                    <Route exact path="/amenities" component={AmenitiesPageContainer} />
                    <Route exact path="/amenities/:id_amenity" component={AmenityEdit} />
                    <Route exact path="/amenity-groups/:id_group" component={AmenityGroupEdit} />

                    <Route
                      exact
                      path="/commercial-ops/price-parity/scheduler/jobs/:jobId"
                      component={ScheduledPriceSummaryJobDetails}
                    />
                    <Route exact path="/commercial-ops/:section?/:subsection?" component={CommercialOpsPage} />

                    <Route exact path="/opportunity/:opportunityId" component={OpportunityContainer} />

                    <Route exact path="/example" component={ExamplePage} />
                    <Route exact path="/site-banner-campaigns" component={SiteBannerCampaignsPage} />
                    <Route exact path="/site-banner-campaigns/create" component={SiteBannerCampaignCreate} />
                    <Route exact path="/site-banner-campaigns/:campaignId" component={SiteBannerCampaignEdit} />
                    <Route exact path="/ai-summary" component={AiSummaryStatsPage} />
                    <Route exact path="/sms-verify-stats" component={SmsVerifyStatsPage} />
                    <Route exact path="/sms-verify-stats/update-agents" component={UpdateCsAgentsPage} />
                    <Route exact path="/users/:id_user/offer-deep-link" component={CreateOfferDeepLinkForm} />
                    <Route exact path="/trip-planner/classic/trip/:tripId/prompts/details" component={CreateTripPage} />
                    <Redirect
                      exact
                      from="/trip-planner/trip/:tripId/prompts/details"
                      to="/trip-planner/classic/trip/:tripId/prompts/details"
                    />
                    <Route exact path="/corporate-events/companies" component={CompaniesListPage} />
                    <Route exact path="/corporate-events/companies/create" component={CompanyFormPage} />
                    <Route exact path="/corporate-events/companies/:id/edit" component={CompanyFormPage} />
                    <Route exact path="/corporate-events/events" component={EventsListPage} />
                    <Route exact path="/corporate-events/events/create" component={EventFormPage} />
                    <Route exact path="/corporate-events/events/:id/edit" component={EventFormPage} />
                    <Redirect from="*" to="/" />
                  </Switch>
                </RouteScanner>
              </SpotlightProvider>
              <AiChatDrawer />
            </AiChatProvider>
          </Authenticated>

          <NotAuthenticated>
            <Route exact path="*" component={LoginPage} />
          </NotAuthenticated>
        </Box>

        <Footer />
      </AppChrome>
    </Box>
  );
}
